import { Flex, Spin } from "antd"
import React, { useEffect } from "react"
import { type ConnectedProps, connect } from "react-redux"

import { type RootState } from "../../../store.ts"
import { fetchSelfDeletionStatus } from "../redux/deletion-lock.thunks.ts"
import AccountDeletionLockPage from "./AccountDeletionLockPage.tsx"

const LOCKED_STATUSES = ["pending", "processing"]

const mapStateToProps = (state: RootState) => ({
  isAuthorized:
    !!state.auth?.accessToken && !!state.auth.userInfo && !state.auth.isAdmin,
  status: state.selfDeletion.status,
  fetched: state.selfDeletion.fetched,
  fetching: state.selfDeletion.fetching
})

const mapDispatchToProps = {
  fetchStatus: fetchSelfDeletionStatus
}

const connector = connect(mapStateToProps, mapDispatchToProps)

interface DeletionLockGateProps extends ConnectedProps<typeof connector> {
  children: React.ReactNode
}

/**
 * Sits above CustomLayout so a locked user never sees the header or sidebar —
 * there is nothing to navigate away with.
 */
const DeletionLockGate = ({
  isAuthorized,
  status,
  fetched,
  fetching,
  fetchStatus,
  children
}: DeletionLockGateProps) => {
  useEffect(() => {
    if (isAuthorized && !fetched && !fetching) {
      fetchStatus()
    }
  }, [isAuthorized, fetched, fetching, fetchStatus])

  // Unauthenticated: let PrivateRoute do the redirecting.
  if (!isAuthorized) {
    return <>{children}</>
  }

  // Never render the app before the status is known — it would flash and then
  // yank a locked user out of a page they already started reading.
  if (!fetched) {
    return (
      <Flex align="center" justify="center" className="min-h-svh w-full">
        <Spin size="large" />
      </Flex>
    )
  }

  if (status && LOCKED_STATUSES.includes(status)) {
    return <AccountDeletionLockPage />
  }

  return <>{children}</>
}

export default connector(DeletionLockGate)
